/**
 * Dialogos de la pantalla de bodegas.
 *
 * Los tres cambian que stock llega a los canales, asi que ninguno actua de un
 * clic: crear pide las coordenadas que SAP no guarda, vincular pide la
 * referencia a mano y deshabilitar explica lo que deja de pasar.
 */
import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent, ReactElement, ReactNode } from 'react';
import type { WarehouseLocationOverrides, WarehouseMapping } from '../api/contracts';
import { ErrorBanner } from './ErrorBanner';

interface DialogFrameProps {
  readonly title: string;
  readonly description: ReactNode;
  readonly confirmLabel: string;
  readonly confirmClassName?: string;
  readonly busy: boolean;
  readonly valid: boolean;
  readonly error: unknown;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly children?: ReactNode;
}

function DialogFrame({
  title,
  description,
  confirmLabel,
  confirmClassName = 'button button--primary',
  busy,
  valid,
  error,
  onConfirm,
  onCancel,
  children,
}: DialogFrameProps): ReactElement {
  const titleId = useId();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (valid && !busy) {
      onConfirm();
    }
  };

  return (
    <div className="dialog__backdrop">
      <form
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={submit}
      >
        <h2 id={titleId}>{title}</h2>
        <div className="muted">{description}</div>
        {children}
        <ErrorBanner error={error} />
        <div className="dialog__actions">
          <button type="button" className="button" onClick={onCancel} disabled={busy}>
            Cancelar
          </button>
          <button type="submit" className={confirmClassName} disabled={busy || !valid}>
            {busy ? 'Enviando…' : confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

/** Numero decimal dentro de un rango, o `null` si el texto no lo es. */
export function parseCoordinate(raw: string, limit: number): number | null {
  const normalized = raw.trim().replace(',', '.');
  if (normalized === '' || !/^-?\d+(\.\d+)?$/.test(normalized)) {
    return null;
  }
  const value = Number(normalized);
  return Math.abs(value) <= limit ? value : null;
}

interface TextFieldProps {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly inputMode?: 'decimal' | 'text';
  readonly invalid?: boolean;
  readonly autoFocus?: boolean;
}

function TextField({
  label,
  value,
  onChange,
  placeholder,
  inputMode = 'text',
  invalid = false,
  autoFocus = false,
}: TextFieldProps): ReactElement {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (autoFocus) {
      ref.current?.focus();
    }
  }, [autoFocus]);
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      <input
        ref={ref}
        className="input"
        value={value}
        placeholder={placeholder}
        inputMode={inputMode}
        aria-invalid={invalid}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

export interface CreateInJanisDialogProps {
  readonly warehouse: WarehouseMapping;
  readonly busy: boolean;
  readonly error: unknown;
  readonly onConfirm: (location: WarehouseLocationOverrides) => void;
  readonly onCancel: () => void;
}

/**
 * Crear la location en Janis. SAP no guarda coordenadas, asi que son lo unico
 * obligatorio aca; la direccion la completa el middleware desde SAP y solo hace
 * falta escribirla si SAP no la tiene (la API responde nombrando lo que falta).
 */
export function CreateInJanisDialog({
  warehouse,
  busy,
  error,
  onConfirm,
  onCancel,
}: CreateInJanisDialogProps): ReactElement {
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');

  const lat = parseCoordinate(latitude, 90);
  const lng = parseCoordinate(longitude, 180);

  const confirm = (): void => {
    if (lat === null || lng === null) {
      return;
    }
    // Un campo vacio se OMITE: mandarlo vacio pisaria el dato que SAP si tiene.
    const address = Object.fromEntries(
      Object.entries({ street, city, state, country })
        .map(([key, value]) => [key, value.trim()] as const)
        .filter(([, value]) => value !== ''),
    );
    onConfirm({
      latitude: lat,
      longitude: lng,
      ...(Object.keys(address).length === 0 ? {} : { address }),
    });
  };

  return (
    <DialogFrame
      title={`Crear ${warehouse.sapWarehouseCode} en Janis`}
      description={
        <p>
          Crea la ubicación y su almacén con la referencia{' '}
          <strong>{warehouse.sapWarehouseCode}</strong>. La bodega queda{' '}
          <strong>deshabilitada</strong>: crear no publica stock.
        </p>
      }
      confirmLabel="Crear en Janis"
      busy={busy}
      valid={lat !== null && lng !== null}
      error={error}
      onConfirm={confirm}
      onCancel={onCancel}
    >
      <div className="filters">
        <TextField
          label="Latitud"
          value={latitude}
          onChange={setLatitude}
          placeholder="-33.4372"
          inputMode="decimal"
          invalid={latitude !== '' && lat === null}
          autoFocus
        />
        <TextField
          label="Longitud"
          value={longitude}
          onChange={setLongitude}
          placeholder="-70.6506"
          inputMode="decimal"
          invalid={longitude !== '' && lng === null}
        />
      </div>
      <p className="muted">
        Dirección opcional: sólo si SAP no la tiene cargada o quieres corregirla.
      </p>
      <div className="filters">
        <TextField label="Calle" value={street} onChange={setStreet} />
        <TextField label="Comuna" value={city} onChange={setCity} />
        <TextField label="Región" value={state} onChange={setState} />
        <TextField label="País" value={country} onChange={setCountry} placeholder="Chile" />
      </div>
    </DialogFrame>
  );
}

export interface LinkReferenceDialogProps {
  readonly warehouse: WarehouseMapping;
  readonly busy: boolean;
  readonly error: unknown;
  readonly onConfirm: (janisWarehouseId: string) => void;
  readonly onCancel: () => void;
}

/**
 * Anotar a mano la referencia de una bodega que YA existe en Janis (creada en
 * otro ambiente o desde el panel de Janis). El middleware no la verifica: si no
 * existe, el `stock-batch` la rechaza en la proxima corrida de F3.
 */
export function LinkReferenceDialog({
  warehouse,
  busy,
  error,
  onConfirm,
  onCancel,
}: LinkReferenceDialogProps): ReactElement {
  const [reference, setReference] = useState(
    warehouse.janisWarehouseId ?? warehouse.sapWarehouseCode,
  );
  const trimmed = reference.trim();
  return (
    <DialogFrame
      title={`Vincular ${warehouse.sapWarehouseCode} con Janis`}
      description={
        <p>
          Úsalo sólo si la bodega ya existe en Janis. Va la <strong>referencia</strong> del almacén
          (su <span className="mono">referenceId</span>), no su id interno. El middleware no la
          comprueba: si Janis no la conoce, la publicación de stock la rechaza.
        </p>
      }
      confirmLabel="Guardar referencia"
      busy={busy}
      valid={trimmed.length > 0 && trimmed.length <= 64}
      error={error}
      onConfirm={() => onConfirm(trimmed)}
      onCancel={onCancel}
    >
      <TextField label="Referencia en Janis" value={reference} onChange={setReference} autoFocus />
      <p className="muted">Lo normal es que coincida con el código de SAP.</p>
    </DialogFrame>
  );
}

export interface DisableWarehouseDialogProps {
  readonly warehouse: WarehouseMapping;
  readonly busy: boolean;
  readonly error: unknown;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export function DisableWarehouseDialog({
  warehouse,
  busy,
  error,
  onConfirm,
  onCancel,
}: DisableWarehouseDialogProps): ReactElement {
  return (
    <DialogFrame
      title={`Deshabilitar ${warehouse.sapWarehouseCode}`}
      description={
        <p>
          El middleware deja de leer y publicar el stock de esta bodega. Janis conserva el último
          stock publicado hasta que alguien lo corrija allá. La referencia se mantiene, así que se
          puede volver a habilitar.
        </p>
      }
      confirmLabel="Deshabilitar"
      confirmClassName="button button--danger"
      busy={busy}
      valid
      error={error}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}
